<script>
    import Util from 'util/util';
    import { fly } from 'svelte/transition';
    import { backInOut } from 'svelte/easing';
    import Button from 'views/components/button/Button.svelte';

    export let language;

    const isTablet = Util.isTablet();

    const characterMapping = {
        de: ['ü', 'ä', 'ß', 'ö'],
        es: ['í', 'ó', 'é', 'á', 'ñ', 'ú', 'ü', '¿', '¡'],
        fr: ['é', 'è', 'ê', 'à', 'â', 'ç', 'ô', 'ù', 'û', 'î', 'œ']
    };

    let isUppercase = false;
    let isVisible = false;
    let currentTarget = null;

    function shouldShowButtons(target) {

        // Never show on tablets
        if (isTablet) {
            return false;
        }

        // TinyMCE: check if the active editor is focused
        if (window?.tinymce?.Editor
            && target instanceof window.tinymce.Editor
        ) {
            return (target.hasFocus() && target?.iframeElement?.closest('.js-taskgroup') !== null)
        }

        if (target === null
            || target === window
        ) {
            return false;
        }

        if (
            target.matches('input[type="text"], textarea')
            || (
                target.isContentEditable
                && target.getAttribute('contenteditable') !== 'false'
            )
        ) {
            return true;
        }

        return false;
    }

    function showToolbar(e) {

        // When an event was triggered by the TinyMCE focusin/focusout, it passes the editor
        // that received this event into the event detail. The target of this event is document
        // which created strange behaviour. Since the detail is not sent by other events we can
        // prefer event detail, and fallback to target.
        const target = e?.detail || e.target;

        if (shouldShowButtons(target)) {
            currentTarget = target;
            isVisible = true;

            // Add extra padding to the body to allow content to scroll past the floating character bar
            // use the formula: 6rem + [height of toolbar]
            document.body.style.paddingBottom = 'calc(6rem + 55px)';
        }
    }

    function hideToolbar() {

        // This kind of a hack. But I'll try to explain the logic:
        // When this method is called, we should unset the current target since the
        // element previously focus on, has lost it focus. However to prevent the
        // floating element from going out and in, and making the user crazy, we
        // add a small delay to give "showToolbar" the time to set the target onto
        // the newly focussed element (if applicable). If the new element shouldn't
        // show the button. We can than safely hide the button. However if the new
        // target also needs the buttons, we don't need to hide the buttons.

        currentTarget = null;
        setTimeout(() => {
            if (!shouldShowButtons(currentTarget)) {
                isVisible = false;

                document.body.style.paddingBottom = null;
            }
        }, 100);
    }

    function insertAtCaret(char) {

        if (!currentTarget) {
            return
        }

        // TinyMCE case
        if (window?.tinymce?.activeEditor && window.tinymce.activeEditor.hasFocus()) {
            const editor = window.tinymce.activeEditor;
            const rng = editor.selection.getRng();
            editor.selection.setRng(rng);
            editor.insertContent(char);
            editor.focus();
            return;
        }

        document.execCommand('insertText', false, char);
    }

    function checkShiftKey(e) {
        isUppercase = e.shiftKey;
    }
</script>

<!-- Hiding the toolbar on focus seems a bit weird, however it's the only way to detect when -->
<!-- the user regained focus outside of tinymce. If the regained focus is in editable element -->
<!-- the hideToolbar won't change anytghing. Therefore it is safe to call -->
<svelte:window
    on:focus={hideToolbar}
    on:tinymce-focusin={showToolbar}
    on:tinymce-focusout={hideToolbar}
    on:keydown={checkShiftKey}
    on:keyup={checkShiftKey}
/>

<svelte:document
    on:focusin={showToolbar}
    on:focusout={hideToolbar}
/>

{#if isVisible}
    <div
        class="char-buttons"
        in:fly={{
            duration: 500,
            y: '200%',
            easing: backInOut
        }}
        out:fly={{
            duration: 200,
            y: '500%',
            easing: backInOut
        }}
    >
        {#each characterMapping[language] as character, i (i)}
            <div on:pointerdown|preventDefault>
                <Button
                    theme="secondary"
                    inline={true}
                    label={isUppercase ? character.toUpperCase() : character}
                    callback={() => insertAtCaret(isUppercase ? character.toUpperCase() : character)}
                ></Button>
            </div>
        {/each}
    </div>
{/if}

<style lang="scss">
    .char-buttons {

        @include box-shadow;
        @include z-index-system-feedback;

        position: fixed;
        left: 0;
        right: 0;
        margin: auto;
        bottom: 6rem;
        transform: translateY(0);
        display: flex;
        align-items: center;
        padding: 8px;
        border-radius: 16px;
        background-color: $blue-black;
        color: $white;
        fill: currentColor;
        width: fit-content;
        gap: 8px;
        max-width: calc(100vw - 16px);
        overflow-x: auto;
    }
</style>
